// Convert PiekAnalyse raw quarter-hour data to a demand profile
// that can be used by the Hub V2 simulation engine.

import type { QuarterData } from '@/types/piekanalyse';

export interface DerivedProfile {
  chp: number[];      // 24-element normalized hourly demand shape
  csf: number[];      // 12-element normalized seasonal factors
  annualMwh: number;  // Total annual demand in MWh
}

/**
 * Derive a CHP (hourly shape), CSF (seasonal factors), and annual MWh
 * from raw quarter-hour electricity data stored in a PiekAnalyse record.
 *
 * @param rawData - Record<dateString, { quarters: QuarterData[] }> from piek_analyses.raw_data
 * @returns DerivedProfile with chp, csf, and annualMwh
 */
export function deriveDemandProfile(
  rawData: Record<string, { quarters: QuarterData[] }>
): DerivedProfile {
  const hourlyTotals = new Array(24).fill(0);
  const hourlyCounts = new Array(24).fill(0);
  const monthlyKwh = new Array(12).fill(0);
  const monthlyDays = new Array(12).fill(0);
  let totalKwh = 0;
  let totalDays = 0;

  for (const [dateStr, { quarters }] of Object.entries(rawData)) {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) continue;

    const month = date.getMonth();
    monthlyDays[month]++;
    totalDays++;

    for (const q of quarters) {
      const hour = Math.floor(q.quarter / 4);
      const kw = q.kWp ?? 0;
      const kwh = kw * 0.25; // kW * 0.25h = kWh per quarter

      hourlyTotals[hour] += kw;
      hourlyCounts[hour]++;
      monthlyKwh[month] += kwh;
      totalKwh += kwh;
    }
  }

  // CHP: average kW per hour, normalized so the mean equals 1
  const hourlyAvg = hourlyTotals.map((t, i) => hourlyCounts[i] > 0 ? t / hourlyCounts[i] : 0);
  const overallHourlyAvg = hourlyAvg.reduce((a, b) => a + b, 0) / 24;
  const chp = hourlyAvg.map(v => overallHourlyAvg > 0 ? v / overallHourlyAvg : 1);

  // CSF: average daily kWh per month, normalized so the mean of active months equals 1
  const monthlyAvgDaily = monthlyKwh.map((t, i) => monthlyDays[i] > 0 ? t / monthlyDays[i] : 0);
  const activeMonths = monthlyAvgDaily.filter(v => v > 0);
  const overallDailyAvg = activeMonths.length > 0
    ? activeMonths.reduce((a, b) => a + b, 0) / activeMonths.length
    : 0;
  const csf = monthlyAvgDaily.map(v => overallDailyAvg > 0 && v > 0 ? v / overallDailyAvg : 1);

  // Annualize: scale to 365.25 days
  const annualMwh = totalDays > 0 ? (totalKwh / totalDays) * 365.25 / 1000 : 0;

  return { chp, csf, annualMwh };
}
